import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Target, Zap, BarChart3, Globe,
  Search, BookOpen, BrainCircuit, TrendingUp, Award,
  Gamepad2, Smartphone, CreditCard, ShieldCheck, Clock,
  GraduationCap, CheckCircle, Briefcase, Timer, Users, ArrowDown, Rocket
} from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import BrandName from "@/components/BrandName";

// --- FAULT FINDING MECHANISM (ERROR BOUNDARY) ---
// This prevents the "Blank White Screen" and instead tells you EXACTLY what broke.
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMessage: "", errorStack: "" };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true };
  }
  componentDidCatch(error, errorInfo) {
    this.setState({
      errorMessage: error.toString(),
      errorStack: errorInfo.componentStack
    });
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: "2rem", backgroundColor: "#ffe6e6", color: "#990000", fontFamily: "monospace", minHeight: "100vh" }}>
          <h1 style={{ fontSize: "2rem", marginBottom: "1rem" }}>🚨 A Fatal Error Occurred!</h1>
          <p><strong>Error Message:</strong> {this.state.errorMessage}</p>
          <details style={{ marginTop: "1rem", whiteSpace: "pre-wrap", background: "#fff", padding: "1rem", border: "1px solid #ccc" }}>
            <summary style={{ cursor: "pointer", fontWeight: "bold", marginBottom: "0.5rem" }}>Click to see exact Component Stack (Where it broke)</summary>
            {this.state.errorStack}
          </details>
          <p style={{ marginTop: "2rem" }}><em>Check your console (F12) for more details. If you see "lucide-react" errors, run <code>npm install lucide-react</code>.</em></p>
        </div>
      );
    }
    return this.props.children;
  }
}

function LandingContent() {
  // Safe Navigation - prevents crashing if outside of <BrowserRouter>
  let safeNavigate;
  try {
    const nav = useNavigate();
    safeNavigate = nav;
  } catch (err) {
    console.warn("React Router is not detected. Using fallback navigation.");
    safeNavigate = (path) => { window.location.href = path; };
  }

  // Smooth scroll handler for Navbar links
  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    // 1. Load Custom Fonts
    const link = document.createElement("link");
    link.href = "https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500;700;800;900&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);

    // 2. Custom Cursor Logic
    const cur = document.getElementById('cur');
    const ring = document.getElementById('cur-ring');
    let mx = 0, my = 0, rx = 0, ry = 0;
    let cursorReq;

    const onMouseMove = (e) => {
      mx = e.clientX;
      my = e.clientY;
      if (cur) {
        cur.style.left = mx + 'px';
        cur.style.top = my + 'px';
      }
    };
    document.addEventListener('mousemove', onMouseMove);

    const loopCursor = () => {
      rx += (mx - rx) * 0.12;
      ry += (my - ry) * 0.12;
      if (ring) {
        ring.style.left = rx + 'px';
        ring.style.top = ry + 'px';
      }
      cursorReq = requestAnimationFrame(loopCursor);
    };
    loopCursor();

    // 3. Orbit Animation Logic (INCREASED SIZE)
    let t = 0;
    let orbitReq;
    const nodes1 = ['n1', 'n2', 'n3'];
    const nodes2 = ['n4', 'n5', 'n6', 'n7'];

    const positionNodes = () => {
      t += 0.008;

      let scale = 1;
      if (window.innerWidth <= 600) scale = 0.5;
      else if (window.innerWidth <= 768) scale = 0.6;
      else if (window.innerWidth <= 900) scale = 0.7;
      else if (window.innerWidth <= 1024) scale = 0.85;

      const cx = 250, cy = 250; // Initial bounds of container
      const r1 = 140, r2 = 220; // Original ring radiuses

      const offsetX = (cx * (1 - scale)) - 32;
      const offsetY = (cy * (1 - scale)) - 32;

      nodes1.forEach((id, i) => {
        const angle = t + (i / nodes1.length) * Math.PI * 2;
        const x = cx + (r1 * Math.cos(angle)) - 32;
        const y = cy + (r1 * Math.sin(angle)) - 32;
        const el = document.getElementById(id);
        // Do not scale raw translation coordinates - the container handles it now
        if (el) { el.style.left = x + 'px'; el.style.top = y + 'px'; el.style.position = 'absolute'; }
      });

      nodes2.forEach((id, i) => {
        const angle = -t * 0.65 + (i / nodes2.length) * Math.PI * 2;
        const x = cx + (r2 * Math.cos(angle)) - 32;
        const y = cy + (r2 * Math.sin(angle)) - 32;
        const el = document.getElementById(id);
        if (el) { el.style.left = x + 'px'; el.style.top = y + 'px'; el.style.position = 'absolute'; }
      });

      orbitReq = requestAnimationFrame(positionNodes);
    };
    positionNodes();

    // 4. Scroll Reveal Logic
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e, i) => {
        if (e.isIntersecting) {
          setTimeout(() => e.target.classList.add('on'), i * 80);
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.12 });

    document.querySelectorAll('.rv').forEach(r => obs.observe(r));

    // Cleanup
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      cancelAnimationFrame(cursorReq);
      cancelAnimationFrame(orbitReq);
      obs.disconnect();
    };
  }, []);

  return (
    <>
      <style>{`
        :root {
          --w: #ffffff;
          --w2: #f8f9fb;
          --w3: #f0f3f8;
          --b1: #0f2a5c;
          --b2: #1a4db5;
          --b3: #3d72e8;
          --bl: #dce9ff;
          --bl2: #eef4ff;
          --o: #f97316;
          --t1: #0d1b2e;
          --t2: #4a5568;
          --t3: #a0aec0;
          --br: #e8edf5;
        }

        *, *::before, *::after { margin:0; padding:0; box-sizing:border-box; }
        html { scroll-behavior:smooth; font-size:16px; }

        body {
          background: var(--w);
          color: var(--t1);
          font-family: 'DM Sans', sans-serif;
          font-weight: 400;
          overflow-x: hidden;
          cursor: none;
        }

        /* ─── CURSOR ─── */
        #cur {
          position: fixed; pointer-events: none; z-index: 9999;
          width: 8px; height: 8px;
          background: var(--o);
          border-radius: 50%;
          transform: translate(-50%,-50%);
          transition: width .25s, height .25s, background .25s;
        }
        #cur-ring {
          position: fixed; pointer-events: none; z-index: 9998;
          width: 28px; height: 28px;
          border: 1px solid rgba(26,77,181,.3);
          border-radius: 50%;
          transform: translate(-50%,-50%);
          transition:all .14s ease;
        }
        body:has(button:hover) #cur,
        body:has(a:hover) #cur { width:14px; height:14px; }

        /* ─── NAV ─── */
        nav {
          position: fixed; top:0; left:0; right:0; z-index:200;
          height: 120px;
          display: flex; align-items: center; justify-content: space-between;
          padding: 0 4rem;
          background: var(--w);
          border-bottom: 1px solid var(--br);
        }
        .n-logo { display:flex; align-items:center; text-decoration:none; cursor:none; }
        .n-logo img { height: 90px; width: auto; }
        .nav-r { display:flex; align-items:center; gap:2.5rem; }
        .nav-r a { font-size:.9rem; font-weight:600; color:var(--t2); text-decoration:none; cursor:none; transition:color .2s; }
        .nav-r a:hover { color:var(--b2); }

        /* ─── HERO ─── */
        .hero {
          min-height: 100vh;
          display: flex; flex-direction:column;
          align-items: center; justify-content: center;
          text-align: center;
          padding: 12rem 2rem 4rem;
          position: relative;
          overflow: hidden;
        }
        .hero::before {
          content:'';
          position:absolute; inset:0;
          background-image: linear-gradient(var(--br) 1px, transparent 1px), linear-gradient(90deg, var(--br) 1px, transparent 1px);
          background-size: 48px 48px;
          opacity:.5;
          pointer-events:none;
        }
        .hero::after {
          content:'';
          position:absolute; top:20%; left:50%; transform:translateX(-50%);
          width:600px; height:300px; border-radius:50%;
          background: radial-gradient(ellipse, rgba(61,114,232,.08) 0%, transparent 70%);
          pointer-events:none;
        }

        .hero-eyebrow {
          display: inline-flex; align-items:center; gap:.6rem;
          font-size:.75rem; font-weight:600; letter-spacing:.18em;
          text-transform:uppercase; color:var(--b2);
          padding:.4rem 1.2rem;
          border: 1px solid var(--bl);
          border-radius:100px;
          background: var(--bl2);
          margin-bottom:2.5rem;
          position:relative; z-index:1;
          opacity:0; animation: up .6s .1s forwards;
        }
        .pulse-dot {
          width:8px; height:8px; border-radius:50%;
          background:var(--o);
          animation: pulse 2s infinite;
        }
        @keyframes pulse { 0%,100%{box-shadow:0 0 0 0 rgba(249,115,22,.4);} 50%{box-shadow:0 0 0 5px rgba(249,115,22,0);} }

        .hero-h {
          font-family:'DM Serif Display', serif;
          font-size: clamp(3.5rem, 8vw, 7.5rem);
          line-height: 1;
          letter-spacing:-.03em;
          color: var(--t1);
          position:relative; z-index:1;
          opacity:0; animation: up .8s .25s forwards;
        }
        .hero-h em { font-style:italic; color:var(--b2); }
        .hero-h .line2 { display:block; color:var(--b1); }
        .hero-h .odot {
          display:inline-block; width:.12em; height:.12em;
          background:var(--o); border-radius:50%;
          margin-left:.06em; vertical-align:super;
        }

        .hero-sub {
          margin-top:2rem;
          font-size:1.2rem; color:var(--t2); font-weight:500;
          max-width:450px; line-height:1.7;
          position:relative; z-index:1;
          opacity:0; animation: up .8s .4s forwards;
        }

        .hero-cta-row {
          margin-top:2.5rem;
          display:flex; justify-content:center; gap: 1.5rem; /* Added gap for spacing between buttons */
          position:relative; z-index:1;
          opacity:0; animation: up .8s .55s forwards;
          flex-wrap: wrap; /* Allows wrapping on very small screens */
        }
        .btn-p {
          font-size:1rem; font-weight:700;
          padding:1rem 3rem; border-radius:10px;
          background:var(--b2); color:white; border:none;
          cursor:none; transition:background .2s, transform .2s;
          box-shadow: 0 4px 16px rgba(26,77,181,.25);
        }
        .btn-p:hover { background:var(--b1); transform:translateY(-2px); }

        /* ─── HERO VISUAL (Larger) ─── */
        .hero-vis {
          position:relative; z-index:1;
          margin-top:6rem; width:100%; max-width:700px;
          opacity:0; animation: fadeIn 1s .7s forwards;
        }
        .orbit-container {
          position:relative; margin:0 auto;
          width:500px; height:500px;
          min-width:500px; min-height:500px; flex-shrink:0;
        }
        .orbit-center {
          position:absolute; top:50%; left:50%;
          transform:translate(-50%,-50%);
          width:100px; height:100px; border-radius:24px;
          background:var(--b2);
          display:flex; align-items:center; justify-content:center;
          box-shadow: 0 8px 32px rgba(26,77,181,.3);
          z-index:10;
        }
        .orbit-ring {
          position:absolute; top:50%; left:50%;
          border-radius:50%; border-style:dashed; border-color:var(--bl);
          transform:translate(-50%,-50%);
        }
        .ring1 { width:280px; height:280px; border-width:1.5px; animation:spin1 18s linear infinite; }
        .ring2 { width:440px; height:440px; border-width:1.5px; animation:spin2 28s linear infinite; }
        @keyframes spin1 { to { transform:translate(-50%,-50%) rotate(360deg); } }
        @keyframes spin2 { to { transform:translate(-50%,-50%) rotate(-360deg); } }

        .onode {
          position:absolute; top:50%; left:50%;
          width:64px; height:64px; border-radius:50%;
          display:flex; flex-direction:column; align-items:center; justify-content:center;
          background:white; border:1px solid var(--br);
          box-shadow:0 4px 16px rgba(26,77,181,.1);
          cursor:none; transform-origin:center;
          transition:box-shadow .2s, transform .15s;
        }
        .onode:hover { box-shadow:0 6px 24px rgba(26,77,181,.2); transform:scale(1.1) !important; }
        .onode-label {
          font-size:.55rem; font-weight:700; color:var(--t3);
          text-transform:uppercase; letter-spacing:.06em;
          margin-top:4px; text-align:center;
          width:70px; line-height:1.2;
        }

        /* ─── STATS ROW ─── */
        .stats-row {
          display:flex; gap:0;
          border:1px solid var(--br); border-radius:16px; overflow:hidden;
          margin-top:5rem; max-width:600px; width:100%;
          box-shadow:0 4px 20px rgba(26,77,181,.06);
          opacity:0; animation: up .8s .85s forwards;
        }
        .stat { flex:1; padding:1.5rem 1rem; text-align:center; border-right:1px solid var(--br); background:white; }
        .stat:last-child { border-right:none; }
        .stat-n { font-family:'DM Sans',sans-serif; font-size:2.8rem; font-weight:800; color:var(--b2); line-height:1; }
        .stat-n span { color:var(--o); font-size:1.6rem; }
        .stat-l { font-size:.75rem; font-weight:700; color:var(--t3); text-transform:uppercase; letter-spacing:.1em; margin-top:.5rem; }

        /* ─── SECTION BASE ─── */
        .sec { padding:8rem 4rem; }
        .sec-label {
          font-size:.8rem; font-weight:700; letter-spacing:.2em;
          text-transform:uppercase; color:var(--b3);
          margin-bottom:.8rem; display:flex; align-items:center; gap:.6rem;
        }
        .sec-label::before { display:none; }
        .sec-h {
          font-family:'DM Sans',sans-serif; font-size:clamp(2.5rem,4.5vw,4rem);
          font-weight:800; line-height:1.1; letter-spacing:-.02em;
          color:var(--t1); margin-bottom:1rem;
        }

        /* ─── WHAT IS LMS ─── */
        .what-sec { background:var(--w2); }
        .what-layout { display:grid; grid-template-columns:1fr 1.2fr; gap:5rem; align-items:center; max-width:1200px; margin:0 auto; }

        .stair-diagram { display:flex; flex-direction:column; gap:1rem; align-items:flex-start; margin-top: 2rem; }
        .stair-step {
          display:flex; align-items:center; gap:1.5rem;
          padding:1.5rem 2rem;
          background:var(--w); border-radius:16px;
          border-left:5px solid var(--b2);
          box-shadow:0 4px 16px rgba(0,0,0,.04);
          transition:transform .25s, border-color .25s;
          width: 100%; max-width: 400px;
        }
        .stair-step:hover { transform:translateY(-3px); border-color:var(--o); }
        .step-num { font-family:'DM Sans',sans-serif; font-size:2rem; font-weight:900; color:var(--b2); min-width:32px; line-height:1; }
        .step-text strong { display:block; font-size:1.3rem; font-weight:800; color:var(--t1); }
        .flow-arrow { color: var(--t3); margin-left: 3rem; animation: bounce 2s infinite; display: flex; align-items: center; justify-content: center; }

        .what-right-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 1.5rem; }
        .what-card-sq {
          background:var(--w); border:1px solid var(--br);
          border-radius:24px; padding:2.5rem 1.5rem;
          transition:border-color .2s, box-shadow .2s, transform .2s;
          display:flex; flex-direction:column; gap:1.2rem; align-items:center; justify-content:center;
          text-align:center; aspect-ratio: 1;
        }
        .what-card-sq:hover { border-color:var(--b3); box-shadow:0 8px 30px rgba(26,77,181,.1); transform:translateY(-4px); }
        .wc-icon { width:72px; height:72px; border-radius:20px; background:var(--bl2); color:var(--b2); display:flex; align-items:center; justify-content:center; }
        .wc-body strong { display:block; font-size:1.25rem; font-weight:800; color:var(--t1); }

        /* ─── POTENTIAL ─── */
        .pot-sec { background:white; }
        .stair-visual-wrap { max-width:900px; margin:3rem auto 0; display:flex; flex-direction:column; gap:0; }
        .stair-step-vis {
          display:grid; grid-template-columns:80px 1fr;
          align-items:center; gap:2rem;
          padding:1.8rem 2.5rem;
          border-radius:16px; border:1px solid transparent;
          transition:background .25s, border-color .25s, transform .25s;
          cursor:none; margin-bottom:8px;
        }
        .stair-step-vis:hover { background:var(--bl2); border-color:var(--bl); transform:translateX(8px); }
        .stair-step-vis:hover .sv-icon-wrap { background:var(--b2); color:white; }

        .sv-num { font-family:'DM Sans',sans-serif; font-size:3.5rem; color:#000; font-weight:900; line-height:1; text-align:center; transition:color .25s; }
        .stair-step-vis:hover .sv-num { color:var(--b2); }
        .sv-body { display:flex; align-items:center; gap:1.5rem; }
        .sv-icon-wrap {
          width:64px; height:64px; border-radius:16px;
          background:var(--w3); color:var(--b2);
          display:flex; align-items:center; justify-content:center;
          flex-shrink:0; transition:background .25s, color .25s;
        }
        .sv-text-title { font-size:1.5rem; font-weight:800; color:var(--t1); }

        /* ─── BENEFITS ─── */
        .ben-sec { background:var(--w2); }
        .ben-grid {
          display:grid;
          grid-template-columns:repeat(5, 1fr);
          gap:1.5rem;
          max-width:1600px; margin:4rem auto 0;
        }
        .ben-card {
          background:white; border:1px solid var(--br);
          border-radius:24px; padding:3rem 1.5rem;
          text-align:center;
          transition:all .25s; cursor:none;
          display:flex; flex-direction:column; align-items:center; justify-content:center;
        }
        .ben-card:hover { border-color:var(--b2); box-shadow:0 8px 24px rgba(26,77,181,.12); transform:translateY(-6px); }
        .bc-icon {
          width:80px; height:80px; border-radius:20px;
          display:flex; align-items:center; justify-content:center;
          background:var(--bl2); color:var(--b2);
          margin-bottom:1.5rem;
        }
        .bc-title { font-size:1.3rem; font-weight:800; color:var(--t1); }

        /* ─── OUTCOMES ─── */
        .out-sec { background:white; }
        .out-grid {
          display:grid; grid-template-columns:repeat(4,1fr);
          gap:1px; background:var(--br);
          border:1px solid var(--br); border-radius:24px; overflow:hidden;
          max-width:1200px; margin:4rem auto 0;
        }
        .oc { background:white; padding:4rem 2rem; text-align:center; transition:background .2s; cursor:none; }
        .oc:hover { background:var(--bl2); }
        .oc-sym { display:flex; justify-content:center; margin-bottom:1.5rem; color:var(--b2); }
        .oc-n { font-family:'DM Sans',sans-serif; font-weight:900; font-size:5rem; line-height:1; color:var(--b2); }
        .oc-n span { color:var(--o); font-size: 3.5rem;}
        .oc-l { font-size:1.2rem; font-weight:800; text-transform:uppercase; letter-spacing:.1em; color:var(--t1); margin-top:1rem; }

        /* ─── JOURNEY ─── */
        .journey-sec { background:var(--w2); }
        .journey-vis {
          display:flex; align-items:flex-start; justify-content:space-between;
          max-width:1200px; margin:5rem auto 0; position:relative;
        }
        .journey-vis::before {
          content:'';
          position:absolute; top:50px; left:50px; right:50px; height:3px;
          background:linear-gradient(90deg, var(--bl) 0%, var(--b3) 100%);
          z-index:0;
        }
        .jv-step {
          flex:1; display:flex; flex-direction:column; align-items:center;
          gap:1.5rem; text-align:center; position:relative; z-index:1; cursor:none;
        }
        .jv-circle {
          width:100px; height:100px; border-radius:50%;
          background:white; border:3px solid var(--br);
          display:flex; align-items:center; justify-content:center;
          color:var(--b2);
          transition:background .25s, border-color .25s, transform .25s, box-shadow .25s, color .25s;
          box-shadow:0 4px 12px rgba(26,77,181,.08);
        }
        .jv-step:hover .jv-circle {
          background:var(--b2); border-color:var(--b2); color:white;
          transform:scale(1.12); box-shadow:0 8px 24px rgba(26,77,181,.25);
        }
        .jv-title { font-size:1.4rem; font-weight:800; color:var(--t1); }

        /* ─── CTA ─── */
        .cta-sec {
          background:var(--b1); padding:10rem 3rem;
          text-align:center; position:relative; overflow:hidden;
        }
        .cta-sec::before {
          content:''; position:absolute; inset:0;
          background: radial-gradient(circle at 20% 50%, rgba(61,114,232,.25) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(249,115,22,.1) 0%, transparent 50%);
          pointer-events:none;
        }
        .cta-sec::after {
          content:'';
          position:absolute; inset:0;
          background-image:
            linear-gradient(rgba(255,255,255,.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,.05) 1px, transparent 1px);
          background-size:48px 48px;
          pointer-events:none;
        }
        .cta-inner { position:relative; z-index:1; }
        .cta-icon { display:flex; justify-content:center; margin-bottom:2.5rem; animation:float 3s ease-in-out infinite; color:white; }
        @keyframes float { 0%,100%{transform:translateY(0);} 50%{transform:translateY(-10px);} }
        .cta-h { font-family:'DM Sans',sans-serif; font-weight:900; font-size:clamp(4rem,7vw,6.5rem); color:white; line-height:1.05; margin-bottom:2rem; }
        .cta-btns { display:flex; justify-content:center; }
        .btn-cta-w { background:white; color:var(--b1); font-size:1.1rem; font-weight:800; padding:1.4rem 4rem; border-radius:12px; border:none; cursor:none; transition:transform .2s,box-shadow .2s; }
        .btn-cta-w:hover { transform:translateY(-2px); box-shadow:0 8px 24px rgba(0,0,0,.25); }

        /* ─── FOOTER ─── */
        footer {
          padding:3.5rem 4rem;
          background: var(--w);
          border-top:1px solid var(--br);
          display:flex; align-items:center; justify-content:space-between;
          flex-wrap:wrap; gap:1.5rem;
        }
        .f-logo { display:flex; align-items:center; gap:1rem; }
        .f-logo img { height: 85px; width: auto; } /* INCREASED FOOTER LOGO SIZE */
        .f-copy { font-size:1rem; color:var(--t3); font-weight:600; margin-left: 1rem;}

        /* ─── ANIMATIONS ─── */
        @keyframes up { from{opacity:0;transform:translateY(24px);} to{opacity:1;transform:translateY(0);} }
        @keyframes fadeIn { from{opacity:0;} to{opacity:1;} }
        @keyframes bounce { 0%, 100% { transform: translateY(-3px); } 50% { transform: translateY(3px); } }
        .rv { opacity:0; transform:translateY(28px); transition:opacity .7s ease, transform .7s ease; }
        .rv.on { opacity:1; transform:none; }
        .rv.d1 { transition-delay:.1s; }
        .rv.d2 { transition-delay:.2s; }

        @media(max-width:1300px) {
          .ben-grid { grid-template-columns: repeat(3, 1fr); }
        }

        @media(max-width:1024px) {
          .out-grid { grid-template-columns: repeat(2, 1fr); }
          .ben-grid { grid-template-columns: repeat(2, 1fr); }
          .orbit-container { transform: scale(0.85); transform-origin: center center; margin-bottom: -30px; }
        }

        @media(max-width:900px) {
          nav { padding:0 1.5rem; height: 90px; } 
          .n-logo img { height: 75px; } 
          .f-logo img { height: 70px; } 
          .nav-r a { display:none; }
          .hero { padding:8rem 1.5rem 2rem; }
          .hero-vis { margin-top: 2rem; }
          
          /* Rather than shrinking widths and breaking the 500x500 JS logic, we scale down */
          .orbit-container { transform: scale(0.7); transform-origin: center top; margin-bottom: -150px; }
          
          .what-layout { grid-template-columns:1fr; gap:3rem; }
          .journey-vis { flex-direction:column; align-items:center; gap:2rem; }
          .journey-vis::before { display:none; }
          .jv-step { width: 100%; max-width: 300px; }
          
          .sec { padding:4rem 1.5rem; }
          footer { padding:2.5rem 1.5rem; flex-direction: column; align-items: flex-start; gap:1.5rem;}
        }

        @media(max-width:768px) {
          .stats-row { flex-direction: column; width: 100%; }
          .stat { border-right: none; border-bottom: 1px solid var(--br); padding: 2rem; }
          .stat:last-child { border-bottom: none; }
          .out-grid { gap: 0; }
          .oc { padding: 2.5rem 1rem; border-bottom: 1px solid var(--br); }
          .orbit-container { transform: scale(0.6); margin-bottom: -200px; }
          .cta-sec { padding: 6rem 1.5rem; }
        }

        @media(max-width:600px) {
          .hero-h { font-size: 3rem; }
          .hero-vis { display: flex; justify-content: center; width: 100%; margin-top: 2rem; }
          .ben-grid { grid-template-columns:1fr; }
          .out-grid { grid-template-columns:1fr; }
          .what-right-grid { grid-template-columns: 1fr; }
          
          .stair-diagram { align-items: center; width: 100%; }
          .stair-step { max-width: 100%; }
          .flow-arrow { margin-left: 0; }
          
          .stair-step-vis { grid-template-columns: 1fr; gap: 1.5rem; align-items: center; text-align: center; padding: 2rem 1.5rem; justify-items: center; border: 1px solid var(--br); box-shadow: 0 4px 16px rgba(0,0,0,0.04); }
          .sv-body { flex-direction: column; align-items: center; gap: 1rem; }
          .sv-num { font-size: 3rem; }
          
          .orbit-container { transform: scale(0.5); margin-bottom: -250px; }
          .cta-h { font-size: 3rem; }
          .btn-cta-w { padding: 1.2rem 2rem; font-size: 1rem; }
        }
      `}</style>

      <div id="cur"></div>
      <div id="cur-ring"></div>

      {/* ─── NAV ─── */}
      <nav>
        <a href="#" className="n-logo" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
          <BrandLogo alt="Institute logo" />
        </a>
        <div className="nav-r">
          <a href="#what-is-lms" onClick={(e) => { e.preventDefault(); scrollTo('what-is-lms'); }}>What is LMS?</a>
          <a href="#potential" onClick={(e) => { e.preventDefault(); scrollTo('potential'); }}>Potential</a>
          <a href="#benefits" onClick={(e) => { e.preventDefault(); scrollTo('benefits'); }}>Benefits</a>
          <a href="#outcomes" onClick={(e) => { e.preventDefault(); scrollTo('outcomes'); }}>Outcomes</a>
        </div>
      </nav>

      {/* ─── HERO ─── */}
      <section className="hero">
        <div className="hero-eyebrow"><span className="pulse-dot"></span> Learning Management System</div>

        {/* RESTORED "Rise. Learn. Lead." HEADING */}
        <h1 className="hero-h">
          <em>Rise.</em>
          <span className="line2">Learn<span className="odot"></span> Lead.</span>
        </h1>

        <p className="hero-sub">One platform. Every skill. Your pace.</p>

        <div className="hero-cta-row">
          <button className="btn-p" onClick={() => safeNavigate('/coursestore')}>Explore our courses</button>
          <button className="btn-p" onClick={() => safeNavigate('/login')}>Start Free →</button>
        </div>

        {/* Orbit visual */}
        <div className="hero-vis">
          <div className="orbit-container" id="orbitContainer">
            {/* Rings */}
            <div className="orbit-ring ring1" id="ring1"></div>
            <div className="orbit-ring ring2" id="ring2"></div>

            {/* Center */}
            <div className="orbit-center">
              <GraduationCap size={56} color="white" />
            </div>

            {/* Ring 1 nodes */}
            <div className="onode" id="n1" title="Courses">
              <BookOpen size={32} color="var(--b2)" />
              <span className="onode-label">Courses</span>
            </div>
            <div className="onode" id="n2" title="Track">
              <BarChart3 size={32} color="var(--b2)" />
              <span className="onode-label">Track</span>
            </div>
            <div className="onode" id="n3" title="Certificate">
              <Award size={32} color="var(--b2)" />
              <span className="onode-label">Certify</span>
            </div>

            {/* Ring 2 nodes */}
            <div className="onode" id="n4" title="Mobile">
              <Smartphone size={32} color="var(--b2)" />
              <span className="onode-label">Mobile</span>
            </div>
            <div className="onode" id="n5" title="Team">
              <Users size={32} color="var(--b2)" />
              <span className="onode-label">Teams</span>
            </div>
            <div className="onode" id="n6" title="AI">
              <BrainCircuit size={32} color="var(--b2)" />
              <span className="onode-label">AI Paths</span>
            </div>
            <div className="onode" id="n7" title="Analytics">
              <TrendingUp size={32} color="var(--b2)" />
              <span className="onode-label">Analytics</span>
            </div>
          </div>
        </div>

        <div className="stats-row">
          <div className="stat"><div className="stat-n">50K<span>+</span></div><div className="stat-l">Learners</div></div>
          <div className="stat"><div className="stat-n">200<span>+</span></div><div className="stat-l">Courses</div></div>
          <div className="stat"><div className="stat-n">94<span>%</span></div><div className="stat-l">Completion</div></div>
        </div>
      </section>

      {/* ─── WHAT IS LMS ─── */}
      <section id="what-is-lms" className="sec what-sec">
        <div className="what-layout">
          <div className="rv">
            <div className="sec-label">What is an LMS?</div>
            <h2 className="sec-h">Your digital<br />classroom everywhere</h2>

            <div className="stair-diagram rv d1">
              <div className="stair-step">
                <div className="step-num">01</div>
                <div className="step-text">
                  <strong>Access Content</strong>
                </div>
              </div>
              <div className="flow-arrow"><ArrowDown size={32} /></div>
              <div className="stair-step">
                <div className="step-num">02</div>
                <div className="step-text">
                  <strong>Track Progress</strong>
                </div>
              </div>
              <div className="flow-arrow"><ArrowDown size={32} /></div>
              <div className="stair-step">
                <div className="step-num">03</div>
                <div className="step-text">
                  <strong>Get Certified</strong>
                </div>
              </div>
              <div className="flow-arrow"><ArrowDown size={32} /></div>
              <div className="stair-step">
                <div className="step-num">04</div>
                <div className="step-text">
                  <strong>Rise Further</strong>
                </div>
              </div>
            </div>
          </div>

          <div className="what-right-grid rv d2">
            <div className="what-card-sq">
              <div className="wc-icon"><Target size={36} /></div>
              <div className="wc-body">
                <strong>One Platform</strong>
              </div>
            </div>
            <div className="what-card-sq">
              <div className="wc-icon"><Zap size={36} /></div>
              <div className="wc-body">
                <strong>Your Pace</strong>
              </div>
            </div>
            <div className="what-card-sq">
              <div className="wc-icon"><BarChart3 size={36} /></div>
              <div className="wc-body">
                <strong>Live Progress</strong>
              </div>
            </div>
            <div className="what-card-sq">
              <div className="wc-icon"><Globe size={36} /></div>
              <div className="wc-body">
                <strong>Any Device</strong>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── POTENTIAL ─── */}
      <section id="potential" className="sec pot-sec">
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <div className="sec-label rv">LMS Potential</div>
          <h2 className="sec-h rv d1">Step by step,<br />unlock everything</h2>
        </div>
        <div className="stair-visual-wrap rv d2">
          <div className="stair-step-vis">
            <div className="sv-num">01</div>
            <div className="sv-body">
              <div className="sv-icon-wrap"><Search size={32} /></div>
              <div className="sv-text-title">Discover</div>
            </div>
          </div>
          <div className="stair-step-vis">
            <div className="sv-num">02</div>
            <div className="sv-body">
              <div className="sv-icon-wrap"><BookOpen size={32} /></div>
              <div className="sv-text-title">Enrol Instantly</div>
            </div>
          </div>
          <div className="stair-step-vis">
            <div className="sv-num">03</div>
            <div className="sv-body">
              <div className="sv-icon-wrap"><BrainCircuit size={32} /></div>
              <div className="sv-text-title">Learn Daily</div>
            </div>
          </div>
          <div className="stair-step-vis">
            <div className="sv-num">04</div>
            <div className="sv-body">
              <div className="sv-icon-wrap"><TrendingUp size={32} /></div>
              <div className="sv-text-title">Track Progress</div>
            </div>
          </div>
          <div className="stair-step-vis">
            <div className="sv-num">05</div>
            <div className="sv-body">
              <div className="sv-icon-wrap"><Award size={32} /></div>
              <div className="sv-text-title">Get Certified</div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── BENEFITS ─── */}
      <section id="benefits" className="sec ben-sec">
        <div style={{ maxWidth: '1500px', margin: '0 auto' }}>
          <div className="sec-label rv">Benefits</div>
          <h2 className="sec-h rv d1">Built for how<br />Gen Z learns</h2>
          <div className="ben-grid rv d2">
            <div className="ben-card">
              <div className="bc-icon"><Clock size={40} /></div>
              <div className="bc-title">Flexible</div>
            </div>
            <div className="ben-card">
              <div className="bc-icon"><Gamepad2 size={40} /></div>
              <div className="bc-title">Gamified</div>
            </div>
            <div className="ben-card">
              <div className="bc-icon"><Smartphone size={40} /></div>
              <div className="bc-title">Mobile-First</div>
            </div>
            <div className="ben-card">
              <div className="bc-icon"><CreditCard size={40} /></div>
              <div className="bc-title">Affordable</div>
            </div>
            <div className="ben-card">
              <div className="bc-icon"><ShieldCheck size={40} /></div>
              <div className="bc-title">Secure</div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── OUTCOMES ─── */}
      <section id="outcomes" className="sec out-sec">
        <div style={{ maxWidth: '1200px', margin: '0 auto', textAlign: 'center' }}>
          <div className="sec-label rv" style={{ justifyContent: 'center' }}>Outcomes</div>
          <h2 className="sec-h rv d1" style={{ textAlign: 'center' }}>Numbers that<br />speak for themselves</h2>
          <div className="out-grid rv d2">
            <div className="oc">
              <div className="oc-sym"><GraduationCap size={64} /></div>
              <div className="oc-n">3<span>×</span></div>
              <div className="oc-l">Faster Skills</div>
            </div>
            <div className="oc">
              <div className="oc-sym"><CheckCircle size={64} /></div>
              <div className="oc-n">94<span>%</span></div>
              <div className="oc-l">Completion</div>
            </div>
            <div className="oc">
              <div className="oc-sym"><Briefcase size={64} /></div>
              <div className="oc-n">78<span>%</span></div>
              <div className="oc-l">Career Uplift</div>
            </div>
            <div className="oc">
              <div className="oc-sym"><Timer size={64} /></div>
              <div className="oc-n">60<span>%</span></div>
              <div className="oc-l">Time Saved</div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── JOURNEY ─── */}
      <section className="sec journey-sec">
        <div style={{ textAlign: 'center', maxWidth: '1200px', margin: '0 auto' }}>
          <div className="sec-label rv" style={{ justifyContent: 'center' }}>The Rise Journey</div>
          <h2 className="sec-h rv d1" style={{ textAlign: 'center' }}>From zero to<br />certified in weeks</h2>
          <div className="journey-vis rv d2">
            <div className="jv-step">
              <div className="jv-circle"><Search size={48} /></div>
              <div className="jv-title">Discover</div>
            </div>
            <div className="jv-step">
              <div className="jv-circle"><BookOpen size={48} /></div>
              <div className="jv-title">Enrol</div>
            </div>
            <div className="jv-step">
              <div className="jv-circle"><Zap size={48} /></div>
              <div className="jv-title">Learn</div>
            </div>
            <div className="jv-step">
              <div className="jv-circle"><BarChart3 size={48} /></div>
              <div className="jv-title">Track</div>
            </div>
            <div className="jv-step">
              <div className="jv-circle"><Award size={48} /></div>
              <div className="jv-title">Rise</div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section className="cta-sec">
        <div className="cta-inner">
          <div className="cta-icon"><Rocket size={80} /></div>
          <h2 className="cta-h rv">Ready to<br />Rise?</h2>
          <div className="cta-btns rv d2">
            <button className="btn-cta-w" onClick={() => safeNavigate('/login')}>Start Learning Free →</button>
          </div>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer>
        <div className="f-logo">
          <BrandLogo alt="Institute logo" />
          <div className="f-copy">© 2026 <BrandName />. All rights reserved.</div>
        </div>
        {/* Completely removed .f-links as requested */}
      </footer>
    </>
  );
}

export default function Landing() {
  return (
    <ErrorBoundary>
      <LandingContent />
    </ErrorBoundary>
  );
}
